import { makeAuthenticatedRequest } from './authService';

// ================================================
// TYPE DEFINITIONS
// ================================================

export interface Article {
  title: string;
  content: string;
  source: string;
  url?: string;
  date?: string;
}

export interface Evaluation {
  score: number;
  summary: string;
  strengths: string[];
  improvements: string[];
  detailedFeedback: string;
}

export interface SubmitResponseData {
  moduleId: string;
  activityId: string;
  exerciseId: string;
  userResponse: string;
  article: Article;
  prompts?: string[];
  exerciseType?: string;
}

export interface ExerciseResponse {
  response_id: string;
  user_id: number;
  module_id: string;
  activity_id: string;
  exercise_id: string;
  user_response: string;
  ai_feedback: Evaluation;
  ai_score: number;
  metadata: any;
  created_at: string;
  updated_at: string;
}

export interface EvaluationResult {
  success: boolean;
  responseId: string;
  evaluation: Evaluation;
  message: string;
}

export interface ExerciseStats {
  total_responses: number;
  average_score: number;
  highest_score: number;
  lowest_score: number;
  exercises_completed: number;
}

export interface HistoryFilters {
  moduleId?: string;
  limit?: number;
  offset?: number;
}

// ================================================
// SERVICE CLASS
// ================================================

class ThinkingExerciseService {
  /**
   * Submit user response and get AI evaluation
   */
  async submitResponse(data: SubmitResponseData): Promise<EvaluationResult> {
    try {
      const response = await makeAuthenticatedRequest('/thinking-exercises/submit', {
        method: 'POST',
        body: JSON.stringify(data)
      });

      if (response.success) {
        return {
          success: true,
          responseId: response.responseId,
          evaluation: response.evaluation,
          message: response.message || 'Response submitted successfully'
        };
      }

      throw new Error(response.message || 'Failed to submit response');
    } catch (error) {
      console.error('Error submitting thinking exercise response:', error);
      throw error;
    }
  }

  /**
   * Get user's most recent response for a specific exercise
   */
  async getResponse(exerciseId: string): Promise<ExerciseResponse | null> {
    try {
      const response = await makeAuthenticatedRequest(
        `/thinking-exercises/${exerciseId}/response`,
        {
          method: 'GET'
        }
      );

      if (response.success) {
        return response.response as ExerciseResponse;
      }

      return null;
    } catch (error) {
      console.error('Error getting thinking exercise response:', error);
      return null;
    }
  }

  /**
   * Get user's response history
   */
  async getHistory(filters?: HistoryFilters): Promise<ExerciseResponse[]> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters?.moduleId) {
        queryParams.append('moduleId', filters.moduleId);
      }
      if (filters?.limit) {
        queryParams.append('limit', filters.limit.toString());
      }
      if (filters?.offset) {
        queryParams.append('offset', filters.offset.toString());
      }

      const queryString = queryParams.toString();
      const url = `/thinking-exercises/history${queryString ? `?${queryString}` : ''}`;

      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });

      if (response.success && response.responses) {
        return response.responses as ExerciseResponse[];
      }

      return [];
    } catch (error) {
      console.error('Error getting thinking exercise history:', error);
      return [];
    }
  }

  /**
   * Get statistics for user's thinking exercises
   */
  async getStats(moduleId?: string): Promise<ExerciseStats | null> {
    try {
      const queryParams = new URLSearchParams();
      
      if (moduleId) {
        queryParams.append('moduleId', moduleId);
      }

      const queryString = queryParams.toString();
      const url = `/thinking-exercises/stats${queryString ? `?${queryString}` : ''}`;

      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });

      if (response.success && response.stats) {
        return response.stats as ExerciseStats;
      }

      return null;
    } catch (error) {
      console.error('Error getting thinking exercise stats:', error);
      return null;
    }
  }

  /**
   * Delete a response
   */
  async deleteResponse(responseId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(
        `/thinking-exercises/${responseId}`,
        {
          method: 'DELETE'
        }
      );

      return response;
    } catch (error) {
      console.error('Error deleting thinking exercise response:', error);
      throw error;
    }
  }

  /**
   * Format score for display with color coding
   */
  getScoreColor(score: number): string {
    if (score >= 90) return '#28a745'; // Excellent - green
    if (score >= 80) return '#5cb85c'; // Good - light green
    if (score >= 70) return '#f0ad4e'; // Fair - orange
    if (score >= 60) return '#ec971f'; // Needs work - dark orange
    return '#d9534f'; // Poor - red
  }

  /**
   * Get score label
   */
  getScoreLabel(score: number): string {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Fair';
    if (score >= 60) return 'Needs Work';
    return 'Keep Practicing';
  }

  /**
   * Validate response before submission
   */
  validateResponse(userResponse: string): { isValid: boolean; error?: string } {
    if (!userResponse || userResponse.trim().length === 0) {
      return { isValid: false, error: 'Response cannot be empty' };
    }

    if (userResponse.trim().length < 50) {
      return { 
        isValid: false, 
        error: 'Response must be at least 50 characters long for meaningful analysis' 
      };
    }

    if (userResponse.length > 5000) {
      return { 
        isValid: false, 
        error: 'Response is too long. Please keep it under 5000 characters' 
      };
    }

    return { isValid: true };
  }

  /**
   * Format article for display
   */
  formatArticle(article: Article): string {
    return `
# ${article.title}

**Source:** ${article.source}${article.date ? ` | **Date:** ${article.date}` : ''}${article.url ? ` | [Read Original](${article.url})` : ''}

---

${article.content}
    `.trim();
  }

  /**
   * Estimate reading time for article
   */
  estimateReadingTime(content: string): number {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  }
}

export const thinkingExerciseService = new ThinkingExerciseService();

